import React, { useRef, useState, useEffect } from 'react';
import { Typography, Box, Grid, Button as MuiButton, CircularProgress, Popover, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import momentPlugin from '@fullcalendar/moment';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import styles from './GameMaster.module.scss';
import './FullCalanderCustom.scss';
import HotGames from '../../components/featured/hot-games/HotGames';
import {
	DeleteGameMasterData,
	GetGameMasterData,
	SaveGameMasterData,
	CreateProgram,
	SaveSelectedProgram,
	GetGameMasterStats,
} from 'core/API/gameMaster';
import { fetchScreenGroups } from 'core/API/ScreenSetting';
import { fetchGracenoteSportsPreferences } from 'core/API/gameMasterSports';
import { useQuery } from 'react-query';
import { ArrowPathIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { deleteEvent } from 'core/API/events';
import { sportMeta } from '../../utils/data';
import { fetchWorkplaceWorkingHours } from 'core/API/workingHours';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useGracenoteContext } from '../../context/gracenoteContext';
import GameMasterLogo from 'assets/images/gameMasterWordIcons.png';
import { jsPDF } from 'jspdf';

const PAGE_MARGIN = 36;
const NEW_PAGE_TOP_EXTRA = 18; // extra space below top of new page so header row doesn't look stuck
const MIN_ROW_HEIGHT = 14;
const CELL_LINE_HEIGHT = 10; // pt per line for wrapped text
const CELL_TOP_PADDING = 5;
const CELL_BOTTOM_PADDING = 5;
const HEADER_FONT_SIZE = 11;
const CELL_FONT_SIZE = 11; // same as header so first page and subsequent pages look consistent
const HEADER_ROW_HEIGHT = 14;
const COLS = { time: 44, sport: 52, screen: 90, channel: 74, event: 192, startEnd: 88 };
const TABLE_WIDTH = COLS.time + COLS.sport + COLS.screen + COLS.channel + COLS.event + COLS.startEnd;
// Data table without Sport column (used when grouping by sport with section headers)
const DATA_COLS = { time: 44, screen: 90, channel: 96, event: 180, startEnd: 88 };
const DATA_TABLE_WIDTH = DATA_COLS.time + DATA_COLS.screen + DATA_COLS.channel + DATA_COLS.event + DATA_COLS.startEnd;
const SECTION_HEADER_ROW_HEIGHT = 16;
const SECTION_HEADER_BG = [240, 244, 248] as const; // light gray-blue like reference
const HEADER_BLUE = [68, 99, 136] as const;
const BORDER_GRAY = 200;
// PDF uses Helvetica (closest to Gotham in jsPDF built-in fonts)
const FONT = 'helvetica';

const GameMaster = () => {
	const { workplaceId, workplace } = useWorkplacesContext();
	const calendarRef = useRef<FullCalendar>(null);
	const [selectedEvent, setSelectedEvent] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [screenGroup, setScreenGroup] = useState('test 1');
	const [formattedEvents, setFormattedEvents] = useState<any[]>([]);
	const [gameMasterData, setGameMasterData] = useState<any>(null);
	const [resources, setResources] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
	const [saveButtonColor, setSaveButtonColor] = useState('#6c757d');
	const [saveButtonCursor, setSaveButtonCursor] = useState('default');
	const [deleteModalTitle, setDeleteModalTitle] = useState('');
	const [sportsData, setSportsData] = useState<any>({});
	const [slotMinTime, setSlotMinTime] = useState('09:00:00');
	const [slotMaxTime, setSlotMaxTime] = useState('20:00:00');
	const [currentDate, setCurrentDate] = useState(moment().startOf('day'));
	const today = moment().startOf('day');
	const { setOffsetDays } = useGracenoteContext(); // ✅ Now it's safe

	// Add Program Modal States
	const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false);
	const [startDatetime, setStartDatetime] = useState('');
	const [endDatetime, setEndDatetime] = useState('');
	const [selectedScreenGroup, setSelectedScreenGroup] = useState('');
	const [selectedScreenGroupId, setSelectedScreenGroupId] = useState('');
	const [selectedFillSports, setSelectedFillSports] = useState<boolean | undefined>(undefined);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Program Selection Modal States
	const [isProgramSelectionModalOpen, setIsProgramSelectionModalOpen] = useState(false);
	const [programsData, setProgramsData] = useState<any[]>([]);
	const [selectedProgram, setSelectedProgram] = useState<any>(null);

	// Stats data state
	const [statsData, setStatsData] = useState<any>(null);
	const [statsAnchorEl, setStatsAnchorEl] = useState<HTMLButtonElement | null>(null);

	// Print options: single choice – with sports (group by sports, preference order) or without sports (flat list)
	const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
	const [printWithSports, setPrintWithSports] = useState(true); // true = group by sports (default), false = without sports
	const lastPrintOptionsRef = useRef<{ reportType: 'new' | 'old'; includeSports: boolean } | null>(null);

	// Sports preferences for sorting print schedule by preference order
	const { data: sportsPreferences = [] } = useQuery(
		['sportsPreferences', workplaceId],
		() => fetchGracenoteSportsPreferences(workplaceId ?? undefined),
		{ enabled: !!workplaceId }
	);

	const isPrevDisabled = currentDate.isSameOrBefore(today.clone().subtract(2, 'day'), 'day');

	const isNextDisabled = currentDate.isSameOrAfter(today.clone().add(3, 'day'), 'day');

	const handleToday = () => {
		if (calendarRef.current) {
			const api = calendarRef.current.getApi();
			api.today();
			setCurrentDate(today.clone());
			api.scrollToTime(moment().subtract(1, 'hours').startOf('hour').format('HH:mm:ss'));
			setOffsetDays(0);
		}
	};

	const handlePrev = () => {
		if (calendarRef.current) {
			const api = calendarRef.current.getApi();
			api.prev();
			const newDate = currentDate.clone().subtract(1, 'day');
			setCurrentDate(newDate);

			const difference = newDate.diff(today, 'days');

			setOffsetDays(difference >= -5 ? difference : -5); // Prevent going beyond -5 days
		}
	};

	const handleNext = () => {
		if (calendarRef.current) {
			const api = calendarRef.current.getApi();
			api.next();
			const newDate = currentDate.clone().add(1, 'day');
			setCurrentDate(newDate);
			const difference = newDate.diff(today, 'days');
			setOffsetDays(difference <= 5 ? difference : 5); // Prevent going beyond +5 days
		}
	};

	// Handle event click
	const handleEventClick = (clickInfo: any) => {
		setSelectedEvent(clickInfo.event);
		console.log(clickInfo.event);
		setScreenGroup(clickInfo.event.getResources()?.[0]?.id);
		setIsModalOpen(true);
	};

	// Handle calendar slot selection (empty slot click)
	const handleSlotSelect = (selectInfo: any) => {
		const start = moment(selectInfo.start);
		const end = start.clone().add(30, 'minutes'); // Predefined 30-minute slot

		// Use dates as-is from FullCalendar (no timezone conversion)
		const startStr = start.format();
		const endStr = end.format();

		setStartDatetime(startStr);
		setEndDatetime(endStr);
		setSelectedScreenGroup(selectInfo.resource?.title || '');
		setSelectedScreenGroupId(selectInfo.resource?.id || '');
		setSelectedFillSports(undefined); // Reset selection
		setIsAddProgramModalOpen(true);
	};

	// Format datetime for display
	const formatDateTime = (isoString: string) => {
		if (!isoString) return '';
		const [date, time] = isoString.split('T');
		const [year, month, day] = date.split('-');
		const formattedTime = time?.split(':').slice(0, 2).join(':');
		return `${month}-${day} ${formattedTime}`;
	};

	// Format datetime for program selection table (YYYY-MM-DD HH:mm format)
	const formatDateTimeForTable = (isoString: string) => {
		if (!isoString) return '';
		const [date, time] = isoString.split('T');
		const [hour, minute] = time?.split(':') || [];
		return `${date} ${hour}:${minute}`;
	};

	// Handle program row selection
	const handleProgramRowSelection = (program: any) => {
		setSelectedProgram(program);
	};

	// Handle save selected program
	const handleSaveSelectedProgram = async () => {
		if (!selectedProgram) {
			alert('Please select a program');
			return;
		}

		try {
			const storedWorkplaceId = Number(localStorage.getItem('workplace-id'));
			const currentWorkplaceId = storedWorkplaceId || (workplaceId ? Number(workplaceId) : 1);
			const tenantName = (localStorage.getItem('BYOD-organizationId') || '').toLowerCase();

			// Map selected program data to API payload format
			const payload = {
				tenant_name: tenantName,
				workplace: {
					id: currentWorkplaceId,
				},
				screen_group: {
					id: selectedProgram.screen_group?.id || Number(selectedScreenGroupId),
				},
				tms_id: selectedProgram.tms_id || '',
				sport: {
					id: selectedProgram.sport?.id || 0,
					label: selectedProgram.sport?.label || '',
				},
				sub_type: selectedProgram.sub_type || '',
				title: selectedProgram.title || '',
				event_title: selectedProgram.event_title || '',
				long_description: selectedProgram.long_description || '',
				duration: selectedProgram.duration || 0,
				call_sign: selectedProgram.call_sign || '',
				channel: selectedProgram.channel || '',
				team_one: selectedProgram.team_one || null,
				team_two: selectedProgram.team_two || null,
				start_time: selectedProgram.start_time || '',
				end_time: selectedProgram.end_time || '',
				game_date: selectedProgram.game_date || '',
				game_time: selectedProgram.game_time || null,
				game_time_zone: selectedProgram.game_time_zone || null,
				scheduled: selectedProgram.scheduled ?? true,
				playoffs: selectedProgram.playoffs ?? false,
				favorite_team: selectedProgram.favorite_team ?? false,
				local_start_time: selectedProgram.local_start_time || selectedProgram.start_time || '',
				local_end_time: selectedProgram.local_end_time || selectedProgram.end_time || '',
			};

			const response = await SaveSelectedProgram(payload);

			if (response.status === 200 || response.status === 201) {
				// Close modal and refresh
				setIsProgramSelectionModalOpen(false);
				setSelectedProgram(null);
				setProgramsData([]);
				setSelectedFillSports(undefined);

				// Refresh the calendar - calls game_master_event_planners API without loader
				// Using refetch() directly (not handleRefresh()) to avoid showing loader
				await refetchGameMaster();
				await refetchScreenGroups();
			} else {
				alert('Failed to save selected program');
			}
		} catch (error: any) {
			console.error('Error saving selected program:', error);
			alert(error?.response?.data?.message || error?.message || 'Failed to save selected program');
		}
	};

	// Handle Add Program submission
	const handleAddProgramSubmit = async () => {
		if (selectedFillSports === undefined) {
			alert('Please select Fill Gap Selection (Sports or Non Sports)');
			return;
		}

		setIsSubmitting(true);
		try {
			const storedWorkplaceId = Number(localStorage.getItem('workplace-id'));
			const currentWorkplaceId = storedWorkplaceId || (workplaceId ? Number(workplaceId) : 1);
			const tenantName = (localStorage.getItem('BYOD-organizationId') || '').toLowerCase();

			const response = await CreateProgram({
				screen_group_name: selectedScreenGroup,
				screen_group_id: Number(selectedScreenGroupId),
				start_time: startDatetime,
				end_time: endDatetime,
				workplace_id: currentWorkplaceId,
				fill_sports: selectedFillSports ?? true,
				tenant_name: tenantName,
			});

      if (response.status === 200 || response.status === 201) {
        // Check if response has data array
        const responseData = response.data?.data || response.data || [];
        
        if (Array.isArray(responseData) && responseData.length > 0) {
          // Show program selection modal with the data
          setProgramsData(responseData);
          setIsAddProgramModalOpen(false);
          setIsProgramSelectionModalOpen(true);
          setSelectedProgram(null);
        } else {
          // No programs found
          setIsAddProgramModalOpen(false);
          setSelectedFillSports(undefined);
          alert("Scheduler was not able to find an event to fill the gaps with the given data");
        }
      } else if (response.status === 400) {
        alert("There are no Events available for the selected time.");
      }
    } catch (error: any) {
      console.error("Error creating program:", error);
      if (error?.response?.status === 400) {
        alert("There are no Events available for the selected time.");
      } else {
        alert(error?.message || "Failed to create program");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

	const onClose = () => setIsModalOpen(false);

	const onDeleteClose = () => setIsDeleteModalOpen(false);

	const mapMemberData = (data: any, selectedDate: moment.Moment) => {
		if (!data?.member) return [];

		const selectedDateString = selectedDate.format('YYYY-MM-DD');

		return data.member
			.filter((event: any) => {
				// Filter events by selected date
				if (!event.businessStartDateTime) return false;
				const eventDateMatch = event.businessStartDateTime.match(/^(\d{4}-\d{2}-\d{2})/);
				if (!eventDateMatch) return false;
				return eventDateMatch[1] === selectedDateString;
			})
			.map((event: any) => {
				function formatSport(sportPath: any) {
					return sportPath?.label || '';
				}

				const formatTime = (time: string) => {
					// Extract just the time part (HH:mm) from ISO string, removing timezone suffix and seconds
					const timeMatch = time.match(/T(\d{2}:\d{2}):\d{2}/);
					return timeMatch ? timeMatch[1] : moment(time).format('HH:mm');
				};

				// Parse localStartTime and localEndTime to extract date and time, then create local date
				// This ensures the calendar shows the exact time without timezone conversion
				const parseLocalTime = (isoString: string) => {
					// Extract date and time from ISO string like "2025-11-25T15:30:00-10:00"
					const match = isoString.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})/);
					if (match) {
						const [, datePart, hour, minute, second] = match;
						// Create a moment object in local timezone with the exact date and time
						// This ensures no timezone conversion happens
						const localMoment = moment(
							`${datePart}T${hour}:${minute}:${second}`,
							'YYYY-MM-DDTHH:mm:ss'
						);
						// Return as ISO string but moment will treat it as local time
						return localMoment.format('YYYY-MM-DDTHH:mm:ss');
					}
					return isoString;
				};

				// Check if it's trivia early (same logic as TvEventCard)
				const isTriviaEvent =
					event?.tmsId?.startsWith('trivia') ||
					event?.tmsId?.toLowerCase().includes('trivia') ||
					event?.callSign === '0' ||
					event?.callSign === 0 ||
					String(event?.callSign) === '0' ||
					event?.sport?.label?.toLowerCase() === 'trivia';

				const sportsData = isTriviaEvent
					? sportMeta['Trivia']
					: sportMeta?.[event?.sport?.label] || sportMeta['Default'];
				console.log('sportsData', sportsData, 'isTriviaEvent', isTriviaEvent, 'event', event);

				// Get label based on eventTitle availability (same logic as EventSpotlight)
				const getEventLabel = () => {
					if (event?.eventTitle) {
						return event.eventTitle;
					} else {
						return event.title;
					}
				};

				return {
					id: event.id,
					title: event.title,
					description: event.longDescription,
					start: parseLocalTime(event.localStartTime),
					end: parseLocalTime(event.localEndTime),
					resourceId: event.screenGroup.id,
					backgroundColor: sportsData.color, // Use actual color value, not CSS class
					borderColor: sportsData.color, // Use actual color value, not CSS class
					className: sportsData.colorScheme, // Add CSS class for additional styling if needed
					extendedProps: {
						channel: event.callSign + '[' + event.channel + ']',
						callSign: event.callSign,
						sport: isTriviaEvent ? 'Trivia' : formatSport(event.sport),
						icon: sportsData.emoji,
						svgUrl: sportsData.svgUrl,
						tmsId: event.tmsId,
						isTrivia: isTriviaEvent,
						startTime: formatTime(event.localStartTime),
						endTime: formatTime(event.localEndTime),
						duration: event.duration,
						eventTitle: getEventLabel(),
						originalEventTitle: event.eventTitle,
						originalTitle: event.title,
					},
				};
			});
	};

	const deleteGameMasterModel = (id: any, title: string) => {
		setDeleteModalTitle(title);
		setIsDeleteModalOpen(true);
	};

	const deleteGameMaster = async (id: any) => {
		try {
			await DeleteGameMasterData({ id: id });
			setIsDeleteModalOpen(false);
			setIsModalOpen(false);
			// Refresh data without showing loader
			await refetchGameMaster();
			await refetchScreenGroups();
		} catch (error) {
			console.error('Error deleting game master:', error);
		}
	};

	const saveEvent = async (id: any, screenId: any) => {
		const data = {
			id,
			screenGroup: `api/game_master_screen_groups/${screenId}`,
		};
		await SaveGameMasterData(data);
		setIsModalOpen(false);
		handleRefresh();
	};

	const screenGroupChange = (e: any) => {
		setSaveButtonColor('#446388');
		setSaveButtonCursor('pointer');
		setSaveButtonDisabled(false);
		setScreenGroup(e.target.value);
	};
	// Get game master data
	const {
		refetch: refetchGameMaster,
		data: gameMasterQueryData,
		isLoading: isLoadingGameMaster,
		isFetching: isFetchingGameMaster,
	} = useQuery(['gameMaster', workplaceId], () => GetGameMasterData(workplaceId), {
		enabled: !!workplaceId,
		refetchInterval: 600000, // 10 minutes
		onSuccess: async (data) => {
			setGameMasterData(data);
		},
		// Keep previous data during refetch to prevent empty state
		keepPreviousData: true,
	});

	// Update gameMasterData when query data changes (handles both initial load and refetch)
	useEffect(() => {
		if (gameMasterQueryData) {
			setGameMasterData(gameMasterQueryData);
		}
	}, [gameMasterQueryData]);

	// Update events when currentDate or gameMasterData changes
	useEffect(() => {
		if (gameMasterData) {
			const mapped = mapMemberData(gameMasterData, currentDate);
			setFormattedEvents(mapped);
		} else {
			// Clear events if no data
			setFormattedEvents([]);
		}
	}, [currentDate, gameMasterData]);

	const {
		refetch: refetchScreenGroups,
		isLoading: isLoadingScreenGroups,
		isFetching: isFetchingScreenGroups,
	} = useQuery(['screenGroups', workplaceId], () => fetchScreenGroups(workplaceId), {
		enabled: !!workplaceId,
		refetchInterval: 600000,
		onSuccess: async (data) => {
			const mappedResources =
				data?.map((group: any) => ({
					id: group.id,
					title: group.label,
					order: group.list_order,
				})) || [];

			setResources(mappedResources);
		},
	});

	// Fetch workplace working hours and update slot times
	useQuery(
		['workplaceWorkingHours', workplaceId, currentDate.format('YYYY-MM-DD')],
		() => {
			const currentWorkplaceId = workplaceId || Number(localStorage.getItem('workplace-id'));
			return fetchWorkplaceWorkingHours(currentWorkplaceId);
		},
		{
			enabled: !!workplaceId || !!Number(localStorage.getItem('workplace-id')),
			refetchInterval: 600000, // 10 minutes
			onSuccess: async (data) => {
				const currentWorkplaceId = workplaceId || Number(localStorage.getItem('workplace-id'));

				if (!currentWorkplaceId || !data?.member) {
					// Use default values if no workplace ID or data
					setSlotMinTime('09:00:00');
					setSlotMaxTime('20:00:00');
					return;
				}

				// Get day of week for the selected date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
				const selectedDayIndex = currentDate.day();

				// Map day index to day labels (API uses: Monday=1, Tuesday=2, etc.)
				const dayMap: { [key: number]: string } = {
					0: 'Sunday',
					1: 'Monday',
					2: 'Tuesday',
					3: 'Wednesday',
					4: 'Thursday',
					5: 'Friday',
					6: 'Saturday',
				};

				const selectedDayLabel = dayMap[selectedDayIndex];

				// Find working hours for selected day
				const selectedDayHours = data.member.find(
					(item: any) => item.day?.label === selectedDayLabel
				);

				if (selectedDayHours && selectedDayHours.openAt && selectedDayHours.closeAt) {
					setSlotMinTime(selectedDayHours.openAt);

					// Calculate slotMaxTime - if closeAt is <= openAt, add 24 hours
					const workTimeStart = selectedDayHours.openAt;
					const workTimeEnd = selectedDayHours.closeAt;

					const [startH] = workTimeStart.split(':').map(Number);
					const [endH, endM = '00', endS = '00'] = workTimeEnd.split(':');
					const parsedEndH = parseInt(endH);

					let calculatedMaxTime;
					if (parsedEndH <= startH) {
						calculatedMaxTime = `${parsedEndH + 24}:${endM}:${endS}`;
					} else {
						calculatedMaxTime = workTimeEnd;
					}

					setSlotMaxTime(calculatedMaxTime);
				} else {
					// Use default values if no hours for selected day
					setSlotMinTime('09:00:00');
					setSlotMaxTime('20:00:00');
				}
			},
			onError: () => {
				// Use default values on error
				setSlotMinTime('09:00:00');
				setSlotMaxTime('20:00:00');
			},
		}
	);

	// Fetch game master stats
	useQuery(
		['gameMasterStats', workplaceId, currentDate.format('YYYY-MM-DD')],
		() => {
			const currentWorkplaceId = workplaceId || Number(localStorage.getItem('workplace-id')) || 1;
			const dateString = currentDate.format('YYYY-MM-DD');
			return GetGameMasterStats(currentWorkplaceId, dateString);
		},
		{
			enabled: !!workplaceId || !!Number(localStorage.getItem('workplace-id')),
			refetchInterval: 600000, // 10 minutes
			onSuccess: async (data) => {
				setStatsData(data);
			},
			onError: (error) => {
				console.error('Error fetching game master stats:', error);
				setStatsData(null);
			},
		}
	);

	// Static resources for screen groups
	// const resources = [
	//   { id: "test1", title: "test 1", order: 1 },
	//   { id: "test2", title: "test 2", order: 2 },
	// ];

	// Refresh FullCalendar whenever formattedEvents updates
	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			calendarApi.removeAllEvents();
			calendarApi.addEventSource(formattedEvents);
		}
	}, [formattedEvents]);

	// Update calendar slot times when they change
	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			calendarApi.setOption('slotMinTime', slotMinTime);
			calendarApi.setOption('slotMaxTime', slotMaxTime);
		}
	}, [slotMinTime, slotMaxTime]);

	// Sync calendar date with currentDate when it changes
	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			const calendarDate = moment(calendarApi.getDate());
			// Always ensure calendar date matches currentDate
			if (!calendarDate.isSame(currentDate, 'day')) {
				calendarApi.gotoDate(currentDate.toDate());
			}
		}
	}, [currentDate]);

	// Force calendar to update when formattedEvents change and ensure date is correct
	useEffect(() => {
		if (calendarRef.current && formattedEvents) {
			const calendarApi = calendarRef.current.getApi();
			const calendarDate = moment(calendarApi.getDate());
			// Ensure calendar is on the correct date when events update
			if (!calendarDate.isSame(currentDate, 'day')) {
				calendarApi.gotoDate(currentDate.toDate());
			}
		}
	}, [formattedEvents, currentDate]);

	const handleRefresh = async () => {
		try {
			setIsLoading(true); // show loading background

			// Navigate calendar to current date first to ensure correct date view
			if (calendarRef.current) {
				const api = calendarRef.current.getApi();
				api.gotoDate(currentDate.toDate());
			}

			// Wait for the refetch to complete
			const result = await refetchGameMaster();
			await refetchScreenGroups();

			// Ensure gameMasterData is updated with refreshed data
			// React Query refetch returns { data, ... } or the data directly
			const refreshedData = result?.data || result;
			if (refreshedData) {
				setGameMasterData(refreshedData);
			}

			// Small delay to ensure state updates propagate, then navigate calendar
			setTimeout(() => {
				if (calendarRef.current) {
					const api = calendarRef.current.getApi();
					// Ensure calendar is on the correct date
					api.gotoDate(currentDate.toDate());
				}
			}, 50);
		} catch (err) {
			console.error('Error refreshing calendar:', err);
		} finally {
			setIsLoading(false); // hide loading screen
		}
	};

	// Build time-slot rows for printed schedule (workday start to end, 30-minute slots)
	const getPrintScheduleRows = () => {
		if (!resources?.length) return [];
		const [startH = 9] = slotMinTime.split(':').map(Number);
		const [endH = 20] = slotMaxTime.split(':').map(Number);
		const sortedResources = [...resources].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
		const slots: {
			timeLabel: string;
			rows: Array<{ sport: string; screenTitle: string; channel: string; eventName: string; timeRange: string }>;
		}[] = [];

		for (let h = startH; h < endH; h++) {
			for (const min of [0, 30]) {
				const slotStart = currentDate.clone().hour(h).minute(min).second(0).millisecond(0);
				const slotEnd = slotStart.clone().add(30, 'minutes');
				const timeLabel = slotStart.format('HH:mm');
				const rows: Array<{ sport: string; screenTitle: string; channel: string; eventName: string; timeRange: string }> = [];

				for (const res of sortedResources) {
					const event = formattedEvents.find(
						(e) =>
							e.resourceId === res.id &&
							moment(e.start).isSameOrAfter(slotStart) &&
							moment(e.start).isBefore(slotEnd)
					);
					if (!event) continue; // skip row when no event
					const props = event.extendedProps || {};
					const sportVal = (props.sport ?? '').trim() || '—';
					const channelVal = (props.channel ?? '').trim();
					const eventNameVal = (props.eventTitle || event.title || '').trim();
					const timeRangeFormatted = `${moment(event.start).format('h:mm A')} – ${moment(event.end).format('h:mm A')}`.trim();
					if (!channelVal && !eventNameVal && !timeRangeFormatted) continue; // remove row if channel, event, start-end all blank
					rows.push({
						sport: sportVal,
						screenTitle: res.title,
						channel: channelVal,
						eventName: eventNameVal,
						timeRange: timeRangeFormatted,
					});
				}
				if (rows.length > 0) slots.push({ timeLabel, rows });
			}
		}
		return slots;
	};

	// Parse "CALLSIGN[34]" into call sign + channel number for print schedule
	const parseChannelForPrint = (channel: string) => {
		if (!channel || channel === '—') return { callSign: channel, channelNumber: null };
		const match = channel.match(/^(.+)\[(\d+)\]$/);
		if (match) return { callSign: match[1].trim(), channelNumber: match[2] };
		return { callSign: channel, channelNumber: null };
	};

	// Safari: thead/tfoot often don't render in print. We add safariPrint class so CSS can apply Safari-only fixes; Windows keeps original thead/tfoot.
	const [isSafari, setIsSafari] = useState(false);
	// Safari: rows per chunk so one chunk fills one A4 page (one header at start, one copyright at end per page).
	const SAFARI_ROWS_PER_PAGE = 48;

	useEffect(() => {
		if (typeof navigator === 'undefined') return;
		const ua = navigator.userAgent;
		const isSafariBrowser =
			(ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium')) ||
			/^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
		setIsSafari(!!isSafariBrowser);
	}, []);

	useEffect(() => {
		refetchGameMaster();
	}, [refetchGameMaster]);

	const printSlots = getPrintScheduleRows();
	// Flatten and sort for print layout. Sport order follows preferences (list_order) when reportType is 'new'.
	type PrintRow = { sport: string; screenTitle: string; channel: string; eventName: string; timeRange: string };

	// Build sport label -> preference order (lower = first). Match report order to Sports Selection preference order.
	// Sort preferences by list_order first so order is correct even if API returns unsorted; use index in sorted list as order.
	const sportOrderMap: Record<string, number> = {};
	const prefsList = (sportsPreferences as Array<{ sport?: { label?: string }; list_order?: number }>).slice();
	prefsList.sort((a, b) => (a.list_order ?? 0) - (b.list_order ?? 0));
	prefsList.forEach((pref, index) => {
		const label = pref.sport?.label?.trim();
		if (label) {
			const key = label.toLowerCase().replace(/\s+/g, ' ').trim();
			sportOrderMap[key] = index;
		}
	});

	// Convert time label (HH:mm or HH:mm:ss) to minutes from midnight for sort by workplace start time
	const timeLabelToMinutes = (label: string): number => {
		if (!label || !label.trim()) return 0;
		const parts = label.trim().split(':').map((p) => parseInt(p, 10) || 0);
		const [h = 0, m = 0] = parts;
		return h * 60 + m;
	};

	// Business day sort key: day starts at workplace start (e.g. 05:25). Times before that (02:00, 04:00) sort after end of day.
	const businessDayStartMinutes = timeLabelToMinutes(slotMinTime);
	const businessDaySortKey = (minutesFromMidnight: number): number =>
		minutesFromMidnight >= businessDayStartMinutes ? minutesFromMidnight : minutesFromMidnight + 24 * 60;

	const getSortedPrintRows = (
		slots: typeof printSlots,
		reportType: 'old' | 'new'
	): Array<{ timeLabel: string; row: PrintRow }> => {
		const flat: Array<{ timeLabel: string; row: PrintRow }> = [];
		slots.forEach(({ timeLabel, rows }) => {
			rows.forEach((row) => flat.push({ timeLabel, row }));
		});
		if (reportType === 'old') {
			// Report by Time: sort by business day (workplace start first, overnight at end), then screen
			flat.sort((a, b) => {
				const keyA = businessDaySortKey(timeLabelToMinutes(a.timeLabel));
				const keyB = businessDaySortKey(timeLabelToMinutes(b.timeLabel));
				if (keyA !== keyB) return keyA - keyB;
				return (a.row.screenTitle ?? '').localeCompare(b.row.screenTitle ?? '');
			});
			return flat;
		}
		// Report by Sport: sort by sport (preference order), then business day time, then screen
		const norm = (s: string) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ').trim() || '—';
		flat.sort((a, b) => {
			const sportA = (a.row.sport ?? '').trim() || '—';
			const sportB = (b.row.sport ?? '').trim() || '—';
			const orderA = sportOrderMap[norm(sportA)] ?? 9999;
			const orderB = sportOrderMap[norm(sportB)] ?? 9999;
			if (orderA !== orderB) return orderA - orderB;
			if (sportA !== sportB) return sportA.localeCompare(sportB);
			// Then by business day (workplace start first, overnight at end)
			const keyA = businessDaySortKey(timeLabelToMinutes(a.timeLabel));
			const keyB = businessDaySortKey(timeLabelToMinutes(b.timeLabel));
			if (keyA !== keyB) return keyA - keyB;
			return (a.row.screenTitle ?? '').localeCompare(b.row.screenTitle ?? '');
		});
		return flat;
	};

	const sortedPrintRows = getSortedPrintRows(printSlots, 'new');

	const copyrightText = `Copyright © ${currentDate.year()} BYOD, Inc. All rights reserved.`;

	// Load logo as base64 for PDF (async)
	const loadLogoDataUrl = (): Promise<string | null> =>
		new Promise((resolve) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => {
				try {
					const canvas = document.createElement('canvas');
					canvas.width = img.naturalWidth;
					canvas.height = img.naturalHeight;
					const ctx = canvas.getContext('2d');
					if (ctx) {
						ctx.drawImage(img, 0, 0);
						resolve(canvas.toDataURL('image/png'));
					} else resolve(null);
				} catch {
					resolve(null);
				}
			};
			img.onerror = () => resolve(null);
			img.src = GameMasterLogo;
		});

	// Generate PDF from schedule data (works on all browsers including Safari; no DOM/canvas).
	// rowsForPdfParam: sorted rows; options: includeSports (section headers) and reportType (affects grouping).
	const generateSchedulePdf = async (
		rowsForPdfParam?: Array<{ timeLabel: string; row: PrintRow }>,
		options?: { includeSports: boolean; reportType: 'old' | 'new' }
	) => {
		const includeSports = options?.includeSports ?? true;
		const reportType = options?.reportType ?? 'new';
		const showSectionHeaders = includeSports && reportType === 'new';

		const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
		const pageW = 595.28;
		const pageH = 841.89;
		const bottomMargin = 40;
		let y = PAGE_MARGIN;

		const trim = (s: string, max: number) =>
			(s ?? '').length > max ? `${(s ?? '').substring(0, max - 1)}…` : (s ?? '—');

		const tableLeft = PAGE_MARGIN;
		// Grouped layout: 5 columns (no Sport column; sport shown as section header)
		const xTime = tableLeft;
		const xScreen = xTime + DATA_COLS.time;
		const xChannel = xScreen + DATA_COLS.screen;
		const xEvent = xChannel + DATA_COLS.channel;
		const xStartEnd = xEvent + DATA_COLS.event;

		// Header: logo + title + date
		const logoDataUrl = await loadLogoDataUrl();
		if (logoDataUrl) {
			const logoW = 120;
			const logoH = 32;
			doc.addImage(logoDataUrl, 'PNG', tableLeft, y, logoW, logoH);
			y += logoH + 14; // extra spacing below logo (reference image)
		}
		doc.setFontSize(12);
		doc.setFont(FONT, 'bold');
		const title = [workplace?.workplaceUnitNumber, workplace?.name].filter(Boolean).join(' ') || 'Schedule';
		doc.text(trim(title, 55), tableLeft, y);
		y += 12;
		// Break line after title, before date – extra margin so line is not stuck to Monday
		doc.setDrawColor(BORDER_GRAY);
		doc.setLineWidth(0.5);
		doc.line(tableLeft, y, tableLeft + DATA_TABLE_WIDTH, y);
		y += 18;
		doc.setFont(FONT, 'normal');
		doc.setFontSize(11);
		doc.setTextColor(60, 60, 60);
		doc.text(currentDate.format('dddd, MMMM D, YYYY'), tableLeft, y);
		doc.setTextColor(0, 0, 0);
		y += 14;
		// Margin below date line (Monday...) before table
		y += 36;

		const drawCopyright = () => {
			doc.setFontSize(8);
			doc.setTextColor(100, 100, 100);
			doc.setFont(FONT, 'normal');
			doc.text(copyrightText, pageW / 2, pageH - 14, { align: 'center' });
			doc.setTextColor(0, 0, 0);
		};

		const drawTableHeader = (yy: number) => {
			doc.setFillColor(...HEADER_BLUE);
			doc.rect(tableLeft, yy, DATA_TABLE_WIDTH, HEADER_ROW_HEIGHT, 'F');
			doc.setDrawColor(BORDER_GRAY);
			doc.setLineWidth(0.3);
			doc.rect(tableLeft, yy, DATA_TABLE_WIDTH, HEADER_ROW_HEIGHT, 'S');
			doc.setFontSize(HEADER_FONT_SIZE);
			doc.setFont(FONT, 'bold');
			doc.setTextColor(255, 255, 255);
			const pad = 4;
			doc.text('Time', xTime + pad, yy + 10);
			doc.text('Screen', xScreen + pad, yy + 10);
			doc.text('Channel', xChannel + pad, yy + 10);
			doc.text('Event', xEvent + pad, yy + 10);
			doc.text('Start – End', xStartEnd + pad, yy + 10);
			doc.setTextColor(0, 0, 0);
			doc.setFont(FONT, 'normal');
		};

		const drawSectionHeader = (yy: number, sportName: string) => {
			doc.setFillColor(...SECTION_HEADER_BG);
			doc.rect(tableLeft, yy, DATA_TABLE_WIDTH, SECTION_HEADER_ROW_HEIGHT, 'F');
			doc.setDrawColor(BORDER_GRAY);
			doc.setLineWidth(0.3);
			doc.rect(tableLeft, yy, DATA_TABLE_WIDTH, SECTION_HEADER_ROW_HEIGHT, 'S');
			doc.setFontSize(HEADER_FONT_SIZE);
			doc.setFont(FONT, 'bold');
			doc.setTextColor(40, 40, 40);
			const label = (sportName || '—').toUpperCase();
			doc.text(trim(label, 80), tableLeft + 6, yy + SECTION_HEADER_ROW_HEIGHT / 2 + 4);
			doc.setTextColor(0, 0, 0);
			doc.setFont(FONT, 'normal');
		};

		const drawRowBorder = (yy: number) => {
			doc.setDrawColor(BORDER_GRAY);
			doc.setLineWidth(0.2);
			doc.line(tableLeft, yy, tableLeft + DATA_TABLE_WIDTH, yy);
		};

		const drawVerticalLines = (rowTop: number, rowBottom: number) => {
			doc.setDrawColor(BORDER_GRAY);
			doc.setLineWidth(0.2);
			const xs = [tableLeft, xScreen, xChannel, xEvent, xStartEnd, tableLeft + DATA_TABLE_WIDTH];
			xs.forEach((x) => doc.line(x, rowTop, x, rowBottom));
		};

		// Group rows by sport only when showing section headers (new report + include sports)
		const sourceRows = rowsForPdfParam ?? sortedPrintRows;
		const sportGroups: Array<{ sport: string; rows: Array<{ timeLabel: string; row: PrintRow }> }> = [];
		if (showSectionHeaders) {
			sourceRows.forEach((item) => {
				const sport = (item.row.sport ?? '').trim() || '—';
				if (sportGroups.length > 0 && sportGroups[sportGroups.length - 1].sport === sport) {
					sportGroups[sportGroups.length - 1].rows.push(item);
				} else {
					sportGroups.push({ sport, rows: [item] });
				}
			});
		}

		drawTableHeader(y);
		y += HEADER_ROW_HEIGHT;
		drawRowBorder(y);
		let dataTop = y;

		doc.setFontSize(CELL_FONT_SIZE);
		const cellPadH = 4;
		const screenMaxW = DATA_COLS.screen - 2 * cellPadH;
		const eventMaxW = DATA_COLS.event - 2 * cellPadH;
		const startEndMaxW = DATA_COLS.startEnd - 2 * cellPadH;
		const baselineOffset = CELL_FONT_SIZE * 0.35;

		if (showSectionHeaders) {
		for (const group of sportGroups) {
			// Section header: sport name (bold, full width)
			if (y + SECTION_HEADER_ROW_HEIGHT > pageH - bottomMargin) {
				drawVerticalLines(dataTop, y);
				drawCopyright();
				doc.addPage();
				y = PAGE_MARGIN + NEW_PAGE_TOP_EXTRA;
				dataTop = y;
				drawTableHeader(y);
				y += HEADER_ROW_HEIGHT;
				drawRowBorder(y);
				dataTop = y;
				doc.setFontSize(CELL_FONT_SIZE);
			}
			drawSectionHeader(y, group.sport);
			y += SECTION_HEADER_ROW_HEIGHT;
			drawRowBorder(y);
			dataTop = y;

			for (const { timeLabel, row } of group.rows) {
				const screenText = (row.screenTitle ?? '').trim() || '';
				const eventText = (row.eventName ?? '').trim() || '';
				const timeRangeText = (row.timeRange ?? '').trim() || '';
				const screenLines = doc.splitTextToSize(screenText, screenMaxW);
				const eventLines = doc.splitTextToSize(eventText, eventMaxW);
				const timeRangeLines = doc.splitTextToSize(timeRangeText, startEndMaxW);
				const contentHeight = Math.max(
					MIN_ROW_HEIGHT - CELL_TOP_PADDING - CELL_BOTTOM_PADDING,
					screenLines.length * CELL_LINE_HEIGHT,
					eventLines.length * CELL_LINE_HEIGHT,
					timeRangeLines.length * CELL_LINE_HEIGHT
				);
				const rowHeight = CELL_TOP_PADDING + contentHeight + CELL_BOTTOM_PADDING;

				if (y + rowHeight > pageH - bottomMargin) {
					drawVerticalLines(dataTop, y);
					drawCopyright();
					doc.addPage();
					y = PAGE_MARGIN + NEW_PAGE_TOP_EXTRA;
					dataTop = y;
					drawTableHeader(y);
					y += HEADER_ROW_HEIGHT;
					drawRowBorder(y);
					dataTop = y;
					doc.setFontSize(CELL_FONT_SIZE);
				}

				const centerY = y + rowHeight / 2 + baselineOffset * 0.5;
				if (timeLabel) {
					doc.setFont(FONT, 'bold');
					doc.text(timeLabel, xTime + cellPadH, centerY);
					doc.setFont(FONT, 'normal');
				} else {
					doc.text('', xTime + cellPadH, centerY);
				}
				const screenBlockH = screenLines.length * CELL_LINE_HEIGHT;
				const screenFirstBaseline = y + (rowHeight - screenBlockH) / 2 + baselineOffset + 1.5;
				screenLines.forEach((line, j) => {
					doc.text(line, xScreen + cellPadH, screenFirstBaseline + j * CELL_LINE_HEIGHT);
				});
				const parsed = row.channel && row.channel !== '—' ? parseChannelForPrint(row.channel) : null;
				if (parsed && parsed.channelNumber != null) {
					const callPart = trim(parsed.callSign + ' ', 14);
					doc.text(callPart, xChannel + cellPadH, centerY);
					const w = doc.getTextWidth(callPart);
					doc.setFont(FONT, 'bold');
					doc.text(parsed.channelNumber, xChannel + cellPadH + w, centerY);
					doc.setFont(FONT, 'normal');
				} else {
					doc.text(parsed && parsed.callSign ? trim(parsed.callSign, 18) : '', xChannel + cellPadH, centerY);
				}
				const eventBlockH = eventLines.length * CELL_LINE_HEIGHT;
				const startEndBlockH = timeRangeLines.length * CELL_LINE_HEIGHT;
				const centerOffset = 1.5;
				const eventFirstBaseline = y + (rowHeight - eventBlockH) / 2 + baselineOffset + centerOffset;
				const startEndFirstBaseline = y + (rowHeight - startEndBlockH) / 2 + baselineOffset + centerOffset;
				eventLines.forEach((line, j) => {
					doc.text(line, xEvent + cellPadH, eventFirstBaseline + j * CELL_LINE_HEIGHT);
				});
				timeRangeLines.forEach((line, j) => {
					doc.text(line, xStartEnd + cellPadH, startEndFirstBaseline + j * CELL_LINE_HEIGHT);
				});

				y += rowHeight;
				drawRowBorder(y);
			}
			// Draw vertical column lines for this section so every group has a proper table grid
			drawVerticalLines(dataTop, y);
		}
		} else {
			// Flat layout: no sport section headers (old report / without sports).
			// Match reference: show Time only on first row of each time block; leave blank for same-time rows.
			let prevTimeLabel: string | null = null;
			for (const item of sourceRows) {
				const { timeLabel, row } = item;
				const screenText = (row.screenTitle ?? '').trim() || '';
				const eventText = (row.eventName ?? '').trim() || '';
				const timeRangeText = (row.timeRange ?? '').trim() || '';
				const screenLines = doc.splitTextToSize(screenText, screenMaxW);
				const eventLines = doc.splitTextToSize(eventText, eventMaxW);
				const timeRangeLines = doc.splitTextToSize(timeRangeText, startEndMaxW);
				const contentHeight = Math.max(
					MIN_ROW_HEIGHT - CELL_TOP_PADDING - CELL_BOTTOM_PADDING,
					screenLines.length * CELL_LINE_HEIGHT,
					eventLines.length * CELL_LINE_HEIGHT,
					timeRangeLines.length * CELL_LINE_HEIGHT
				);
				const rowHeight = CELL_TOP_PADDING + contentHeight + CELL_BOTTOM_PADDING;

				if (y + rowHeight > pageH - bottomMargin) {
					drawVerticalLines(dataTop, y);
					drawCopyright();
					doc.addPage();
					y = PAGE_MARGIN + NEW_PAGE_TOP_EXTRA;
					dataTop = y;
					drawTableHeader(y);
					y += HEADER_ROW_HEIGHT;
					drawRowBorder(y);
					dataTop = y;
					doc.setFontSize(CELL_FONT_SIZE);
					prevTimeLabel = null; // reset when new page so first row on page shows time if new block
				}

				const centerY = y + rowHeight / 2 + baselineOffset * 0.5;
				// Show time only for first row of each time block (reference layout)
				const showTimeInCell = timeLabel !== prevTimeLabel;
				prevTimeLabel = timeLabel;
				if (showTimeInCell && timeLabel) {
					doc.setFont(FONT, 'bold');
					doc.text(timeLabel, xTime + cellPadH, centerY);
					doc.setFont(FONT, 'normal');
				} else {
					doc.text('', xTime + cellPadH, centerY);
				}
				const screenBlockH = screenLines.length * CELL_LINE_HEIGHT;
				const screenFirstBaseline = y + (rowHeight - screenBlockH) / 2 + baselineOffset + 1.5;
				screenLines.forEach((line, j) => {
					doc.text(line, xScreen + cellPadH, screenFirstBaseline + j * CELL_LINE_HEIGHT);
				});
				const parsed = row.channel && row.channel !== '—' ? parseChannelForPrint(row.channel) : null;
				if (parsed && parsed.channelNumber != null) {
					const callPart = trim(parsed.callSign + ' ', 14);
					doc.text(callPart, xChannel + cellPadH, centerY);
					const w = doc.getTextWidth(callPart);
					doc.setFont(FONT, 'bold');
					doc.text(parsed.channelNumber, xChannel + cellPadH + w, centerY);
					doc.setFont(FONT, 'normal');
				} else {
					doc.text(parsed && parsed.callSign ? trim(parsed.callSign, 18) : '', xChannel + cellPadH, centerY);
				}
				const eventBlockH = eventLines.length * CELL_LINE_HEIGHT;
				const startEndBlockH = timeRangeLines.length * CELL_LINE_HEIGHT;
				const centerOffset = 1.5;
				const eventFirstBaseline = y + (rowHeight - eventBlockH) / 2 + baselineOffset + centerOffset;
				const startEndFirstBaseline = y + (rowHeight - startEndBlockH) / 2 + baselineOffset + centerOffset;
				eventLines.forEach((line, j) => {
					doc.text(line, xEvent + cellPadH, eventFirstBaseline + j * CELL_LINE_HEIGHT);
				});
				timeRangeLines.forEach((line, j) => {
					doc.text(line, xStartEnd + cellPadH, startEndFirstBaseline + j * CELL_LINE_HEIGHT);
				});

				y += rowHeight;
				drawRowBorder(y);
			}
			drawVerticalLines(dataTop, y);
		}
		drawCopyright();

		return doc;
	};

	// Print preview modal: show PDF in iframe so user can print via browser (works on Safari + all browsers)
	const [printPreviewUrl, setPrintPreviewUrl] = useState<string | null>(null);
	const printIframeRef = useRef<HTMLIFrameElement>(null);

	const handlePrintSchedule = async () => {
		const slots = getPrintScheduleRows();
		// Second option (without sports): time-based sort like before. First option: group by sports, preference order.
		const reportType: 'new' | 'old' = printWithSports ? 'new' : 'old';
		const rowsForPdf = getSortedPrintRows(slots, reportType);
		const options = { includeSports: printWithSports, reportType };
		lastPrintOptionsRef.current = options;
		const doc = await generateSchedulePdf(rowsForPdf, options);
		const blob = doc.output('blob');
		const url = URL.createObjectURL(blob);
		setPrintPreviewUrl(url);
	};

	const closePrintPreview = () => {
		if (printPreviewUrl) {
			URL.revokeObjectURL(printPreviewUrl);
			setPrintPreviewUrl(null);
		}
	};

	const downloadPdfFromPreview = async () => {
		const opts = lastPrintOptionsRef.current ?? { includeSports: true, reportType: 'new' as const };
		const slots = getPrintScheduleRows();
		const rowsForPdf = getSortedPrintRows(slots, opts.reportType);
		const doc = await generateSchedulePdf(rowsForPdf, opts);
		const filename = `Mabel_${currentDate.format('MM_DD_YYYY')}.pdf`;
		const blob = doc.output('blob');
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	};

	// Group by sport for section headers (reference layout)
	const sportGroupsForPrint: Array<{ sport: string; rows: Array<{ timeLabel: string; row: PrintRow }> }> = [];
	sortedPrintRows.forEach((item) => {
		const sport = (item.row.sport ?? '').trim() || '—';
		if (sportGroupsForPrint.length > 0 && sportGroupsForPrint[sportGroupsForPrint.length - 1].sport === sport) {
			sportGroupsForPrint[sportGroupsForPrint.length - 1].rows.push(item);
		} else {
			sportGroupsForPrint.push({ sport, rows: [item] });
		}
	});

	type PrintChunkItem = { type: 'section'; sport: string } | { type: 'row'; timeLabel: string; row: PrintRow };
	interface PrintChunk {
		key: string;
		items: PrintChunkItem[];
	}
	const printItems: PrintChunkItem[] = [];
	sportGroupsForPrint.forEach((group) => {
		printItems.push({ type: 'section', sport: group.sport });
		group.rows.forEach(({ timeLabel, row }) => printItems.push({ type: 'row', timeLabel, row }));
	});

	const safariChunks: PrintChunk[] = [];
	if (printItems.length > 0) {
		const n = SAFARI_ROWS_PER_PAGE;
		for (let i = 0; i < printItems.length; i += n) {
			safariChunks.push({
				key: `chunk-${i}`,
				items: printItems.slice(i, Math.min(i + n, printItems.length)),
			});
		}
	}

	const renderSportSectionHeader = (sport: string, key: string): React.ReactElement => (
		<tr key={key} className={styles.printScheduleSportHeader}>
			<td colSpan={5}>{(sport || '—').toUpperCase()}</td>
		</tr>
	);

	const renderDataRow = (timeLabel: string, row: PrintRow, key: string, rowParity: 'odd' | 'even'): React.ReactElement => (
		<tr key={key} className={rowParity === 'even' ? styles.printScheduleDataRowEven : undefined}>
			<td className={styles.printScheduleTime}>{timeLabel}</td>
			<td>{row.screenTitle}</td>
			<td>
				{(() => {
					const { callSign, channelNumber } = parseChannelForPrint(row.channel);
					if (channelNumber != null) {
						return (
							<>
								{callSign}{' '}
								<span className={styles.printScheduleChannelNumber}>{channelNumber}</span>
							</>
						);
					}
					return callSign;
				})()}
			</td>
			<td>{row.eventName}</td>
			<td className={styles.printScheduleStartEnd}>{row.timeRange}</td>
		</tr>
	);

	const printTableHeader = () => (
		<tr>
			<th>Time</th>
			<th>Screen</th>
			<th>Channel</th>
			<th>Event</th>
			<th className={styles.printScheduleStartEnd}>Start – End</th>
		</tr>
	);

	const printCopyrightRow = (key: string) => (
		<tr key={key} className={styles.printScheduleCopyrightRow}>
			<td colSpan={5} className={styles.printScheduleTableFooter}>
				{copyrightText}
			</td>
		</tr>
	);

	return (
		<>
			{/* Chunk-based layout on all browsers so Windows matches Safari (one header + copyright per page) */}
			<div className={`${styles.printScheduleOnly} ${safariChunks.length > 0 ? styles.safariPrint : ''}`} aria-hidden="true">
				<div className={styles.printScheduleContent}>
					<header className={styles.printScheduleHeader}>
						<img src={GameMasterLogo} alt="GameMaster" className={styles.printScheduleLogo} />
						<p className={styles.printScheduleHeaderDetails}>
							{[workplace?.workplaceUnitNumber, workplace?.name].filter(Boolean).join(' ')}
							{' '}
							{currentDate.format('M/D/YYYY')}
						</p>
					</header>
					<p className={styles.printScheduleDate}>{currentDate.format('dddd, MMMM D, YYYY')}</p>
					{safariChunks.length > 0 ? (
						safariChunks.map((chunk: PrintChunk) => {
							let dataRowIndex = 0;
							return (
								<div key={chunk.key} className={styles.printScheduleChunk}>
									<table className={styles.printScheduleTable}>
										<colgroup>
											<col style={{ width: '9%' }} />
											<col style={{ width: '18%' }} />
											<col style={{ width: '20%' }} />
											<col style={{ width: '36%' }} />
											<col style={{ width: '17%' }} />
										</colgroup>
										<thead>{printTableHeader()}</thead>
										<tbody>
											{chunk.items.map((item, j) =>
												item.type === 'section' ? (
													renderSportSectionHeader(item.sport, `${chunk.key}-sec-${j}`)
												) : (
													(() => {
														dataRowIndex += 1;
														const parity: 'odd' | 'even' = dataRowIndex % 2 === 0 ? 'even' : 'odd';
														return renderDataRow(item.timeLabel, item.row, `${chunk.key}-${j}`, parity);
													})()
												)
											)}
											{printCopyrightRow(`${chunk.key}-c`)}
										</tbody>
									</table>
								</div>
							);
						})
					) : (
						<table className={styles.printScheduleTable}>
							<colgroup>
								<col style={{ width: '9%' }} />
								<col style={{ width: '18%' }} />
								<col style={{ width: '20%' }} />
								<col style={{ width: '36%' }} />
								<col style={{ width: '17%' }} />
							</colgroup>
							<thead>
								{printTableHeader()}
							</thead>
							<tbody>
								{(() => {
									let dataRowIndex = 0;
									return sportGroupsForPrint.map((group, gi) => (
										<React.Fragment key={`g-${gi}`}>
											{renderSportSectionHeader(group.sport, `sec-${gi}`)}
											{group.rows.map(({ timeLabel, row }, ri) => {
												dataRowIndex += 1;
												const parity: 'odd' | 'even' = dataRowIndex % 2 === 0 ? 'even' : 'odd';
												return renderDataRow(timeLabel, row, `r-${gi}-${ri}`, parity);
											})}
										</React.Fragment>
									));
								})()}
							</tbody>
							<tfoot>
								<tr>
									<td colSpan={5} className={styles.printScheduleTableFooter}>
										{copyrightText}
									</td>
								</tr>
							</tfoot>
						</table>
					)}
				</div>
			</div>

			<div className={styles.gamemaster_page}>
			<div className={styles.dashboardContainer}>
				<Grid container spacing={2} className={styles.topRow}>
					<Grid item xs={12} md={6}>
						<Box sx={{ height: '350px' }}>
							<HotGames />
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<div
							className={`${styles.card} ${styles.whiteCard}`}
							style={{ height: '350px', display: 'flex', flexDirection: 'column', padding: '10px' }}
						>
							<div
								className={styles.notesHeader}
								style={{ backgroundColor: '#fff', flexShrink: 0, padding: '0 10px' }}
							>
								<Typography variant="h6" sx={{ py: 1, m: 0 }}>
									Daily Statistics
								</Typography>
							</div>

							<Box
								className={styles.scrollableContent}
								sx={{
									flex: 1,
									overflow: 'hidden',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								{statsData?.stats ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: { xs: 'column', xl: 'row' },
											gap: { xs: 1, xl: 2 },
											alignItems: { xs: 'flex-start', xl: 'center' },
											height: '100%',
											width: '100%',
											justifyContent: 'center',
											padding: { xs: '10px', xl: '0 20px' },
										}}
									>
										{/* Stats Box - Only visible on xl screens and above */}
										<Box
											sx={{
												display: { xs: 'none', xl: 'flex' },
												flexDirection: 'column',
												gap: 2,
												flexShrink: 0,
											}}
										>
											<Typography
												variant="body2"
												sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.6 }}
											>
												Total Games Shown:{' '}
												<strong style={{ color: 'black' }}>
													{statsData.stats.total_games_shown || 0}
												</strong>
											</Typography>
											<Typography
												variant="body2"
												sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.6 }}
											>
												Acceptance Rate (Alerts):{' '}
												<strong style={{ color: 'black' }}>
													{statsData.stats.acceptance_rate_percentage || 0}%
												</strong>
											</Typography>
											<Typography
												variant="body2"
												sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.6 }}
											>
												TimeOut Rate (Alerts):{' '}
												<strong style={{ color: 'black' }}>
													{statsData.stats.timeout_rate_percentage || 0}%
												</strong>
											</Typography>
											<Typography
												variant="body2"
												sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.6 }}
											>
												Favorite Teams (Games Shown):{' '}
												<strong style={{ color: 'black' }}>
													{statsData.stats.number_of_favorite_teams_shown || 0}
												</strong>
											</Typography>
										</Box>

										{/* Info Button - Only visible on screens below xl, positioned at top */}
										<Box sx={{ display: { xs: 'flex', xl: 'none' }, alignItems: 'flex-start', width: '100%', justifyContent: 'flex-start', pt: 4 }}>
											<Tooltip title="View Statistics" arrow placement="right">
											<IconButton
												onClick={(event) => setStatsAnchorEl(event.currentTarget)}
												sx={{
													color: '#446388',
													backgroundColor: '#f0f4f8',
													'&:hover': {
														backgroundColor: '#e0e7ee',
													},
												}}
											>
												<InfoIcon />
											</IconButton>
										</Tooltip>
									</Box>

									<Box
										sx={{
												flex: 1,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												height: '100%',
												width: '100%',
												marginTop: { xs: 0, xl: '-22px' },
											}}
										>
											{statsData.stats.sport_distribution &&
											statsData.stats.sport_distribution.length > 0 ? (
												<Box
													sx={{
														position: 'relative',
														width: '100%',
														height: '100%',
														minHeight: '260px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
													}}
												>
													<ResponsiveContainer width="100%" height="100%">
														<PieChart>
															<Pie
																data={statsData.stats.sport_distribution}
																cx="50%"
																cy="50%"
																labelLine={true}
																outerRadius={95}
																innerRadius={55}
																fill="#8884d8"
																dataKey="percentage"
																paddingAngle={2}
																label={({
																	cx,
																	cy,
																	midAngle,
																	innerRadius,
																	outerRadius,
																	sport,
																	percentage,
																	percent,
																	payload,
																}: any) => {
																	const RADIAN = Math.PI / 180;
																	// Move labels outside the chart
																	const radius = outerRadius * 1.3;
																	const x = cx + radius * Math.cos(-midAngle * RADIAN);
																	const y = cy + radius * Math.sin(-midAngle * RADIAN);

																	// Get sport name and percentage value
																	const sportName = sport || payload?.sport || '';
																	const percentValue =
																		percentage !== undefined
																			? percentage
																			: percent
																				? percent * 100
																				: 0;

																	// Get color from sportMeta based on sport name
																	const sportColor =
																		sportMeta[sportName]?.color ||
																		sportMeta['Default']?.color ||
																		'#6b7280';

																	// Only render if we have valid data
																	if (
																		!sportName ||
																		percentValue === undefined ||
																		isNaN(percentValue)
																	)
																		return null;

																	// Calculate line points
																	const outerX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
																	const outerY = cy + outerRadius * Math.sin(-midAngle * RADIAN);

																	return (
																		<g>
																			<line
																				x1={outerX}
																				y1={outerY}
																				x2={x}
																				y2={y}
																				stroke="black"
																				strokeWidth={1}
																			/>
																			<text
																				x={x}
																				y={y}
																				fill="black"
																				textAnchor={x > cx ? 'start' : 'end'}
																				dominantBaseline="central"
																				fontSize="13"
																				fontWeight="normal"
																				style={{
																					zIndex: 1000,
																					pointerEvents: 'none',
																					fontFamily: 'inherit',
																				}}
																			>
																				{`${sportName} ${percentValue.toFixed(1)}%`}
																			</text>
																		</g>
																	);
																}}
															>
																{statsData.stats.sport_distribution.map(
																	(entry: any, index: number) => {
																		// Get color from sportMeta based on sport name
																		const sportName = entry.sport || '';
																		const sportColor =
																			sportMeta[sportName]?.color ||
																			sportMeta['Default']?.color ||
																			'#6b7280';
																		return (
																			<Cell
																				key={`cell-${index}`}
																				fill={sportColor}
																				stroke={sportColor}
																				strokeWidth={1.5}
																			/>
																		);
																	}
																)}
															</Pie>
														</PieChart>
														<style>
															{`
																.recharts-pie-label-line {
																	stroke-width: 1;
																}
															`}
														</style>
													</ResponsiveContainer>
													<Box
														sx={{
															position: 'absolute',
															top: '50%',
															left: '50%',
															transform: 'translate(-50%, -50%)',
															textAlign: 'center',
															pointerEvents: 'none',
															width: '110px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															zIndex: 1000,
														}}
													>
														<Typography
															variant="caption"
															sx={{
																fontSize: '13px',
																fontWeight: 600,
																color: '#333',
																lineHeight: 1.3,
																zIndex: 1001,
															}}
														>
															Game Distribution
														</Typography>
													</Box>
												</Box>
											) : (
												<Box
													sx={{
														width: 120,
														height: 120,
														borderRadius: '50%',
														background: '#f5f5f5',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														position: 'relative',
													}}
												>
													<Box
														sx={{
															width: 60,
															height: 60,
															borderRadius: '50%',
															backgroundColor: '#fff',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}
													>
														<Typography
															variant="caption"
															sx={{ fontSize: '10px', textAlign: 'center' }}
														>
															Game Distribution
															<br />
															(By Sport)
														</Typography>
													</Box>
												</Box>
											)}
										</Box>
									</Box>
								) : (
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											padding: '20px',
											flex: 1,
										}}
									>
										<CircularProgress size={20} />
									</Box>
								)}
							</Box>
						</div>
					</Grid>
				</Grid>

				{/* Stats Info Popover */}
				<Popover
					open={Boolean(statsAnchorEl)}
					anchorEl={statsAnchorEl}
					onClose={() => setStatsAnchorEl(null)}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
				>
					<Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
						{/* <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#446388' }}>
							Daily Statistics
						</Typography> */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
							<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', lineHeight: 1.6 }}>
								Total Games Shown:{' '}
								<strong style={{ color: '#446388' }}>
									{statsData?.stats?.total_games_shown || 0}
								</strong>
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', lineHeight: 1.6 }}>
								Acceptance Rate (Alerts):{' '}
								<strong style={{ color: '#446388' }}>
									{statsData?.stats?.acceptance_rate_percentage || 0}%
								</strong>
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', lineHeight: 1.6 }}>
								TimeOut Rate (Alerts):{' '}
								<strong style={{ color: '#446388' }}>
									{statsData?.stats?.timeout_rate_percentage || 0}%
								</strong>
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', lineHeight: 1.6 }}>
								Favorite Teams (Games Shown):{' '}
								<strong style={{ color: '#446388' }}>
									{statsData?.stats?.number_of_favorite_teams_shown || 0}
								</strong>
							</Typography>
						</Box>
					</Box>
				</Popover>

				{/* Schedule Section */}
				<div className={`${styles.card} ${styles.whiteCard} ${styles.scheduleCard}`}>
					<div className={styles.notesHeader} style={{ backgroundColor: '#fff', padding: '13px' }}>
						<Typography variant="h6" sx={{ p: 0.7, flexShrink: 0 }}>
							Schedule
						</Typography>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minWidth: 0 }}>
							<MuiButton onClick={handleToday} disabled={currentDate.isSame(today, 'day')}>
								Today
							</MuiButton>
							<MuiButton onClick={handlePrev} disabled={isPrevDisabled}>
								<ChevronLeftIcon className="w-5 h-5" />
							</MuiButton>
							<span style={{ marginLeft: '10px' }}>{currentDate.format('MMMM D, YYYY')}</span>
							<MuiButton onClick={handleNext} disabled={isNextDisabled}>
								<ChevronRightIcon className="w-5 h-5" />
							</MuiButton>
						</div>
						<div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '8px' }}>
							<button
								onClick={() => setPrintOptionsOpen(true)}
								style={{
									backgroundColor: 'rgb(68, 99, 136)',
									color: 'white',
									padding: '5px 15px',
									borderRadius: '4px',
									fontSize: '14px',
									cursor: 'pointer',
									border: 'none',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '6px',
									textTransform: 'capitalize',
									minWidth: '92px',
								}}
							>
								Print
							</button>
							<button
								onClick={() => handleRefresh()}
								style={{
									backgroundColor: 'rgb(68, 99, 136)',
									color: 'white',
									padding: '5px 15px',
									borderRadius: '4px',
									fontSize: '14px',
									cursor: 'pointer',
									border: 'none',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '6px',
									textTransform: 'capitalize',
									minWidth: '92px',
								}}
							>
								<ArrowPathIcon className="w-4 h-4" />
								Refresh
							</button>
						</div>
					</div>

					<div className={styles.scheduleContainer}>
						{!workplaceId ||
						isLoading ||
						isLoadingGameMaster ||
						isFetchingGameMaster ||
						isLoadingScreenGroups ||
						isFetchingScreenGroups ? (
							// Show loading progress wheel during API calls or when workplaceId is not available
							<div
								style={{
									width: '100%',
									height: '600px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<CircularProgress size={60} />
							</div>
						) : workplaceId &&
						  (!formattedEvents || formattedEvents.length === 0) &&
						  (!resources || resources.length === 0) ? (
							// Show no data message only after API response is received and data is empty
							<div
								style={{
									width: '100%',
									height: '600px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexDirection: 'column',
									gap: '16px',
									marginTop: '-100px',
								}}
							>
								<Typography
									variant="h6"
									style={{
										color: 'rgb(68, 99, 136)',
										fontWeight: 500,
										textAlign: 'center',
									}}
								>
									No schedule generated. Please add a Providers, Channels, Sports, and Screen Groups
									to continue from GameMaster Setting.
								</Typography>
							</div>
						) : (
							<FullCalendar
								ref={calendarRef}
								plugins={[
									resourceTimelinePlugin,
									momentPlugin,
									interactionPlugin,
									momentTimezonePlugin,
								]}
								initialView="resourceTimelineDay"
								initialDate={currentDate.toDate()}
								timeZone="local"
								scrollTime="10:00:00"
								headerToolbar={false}
								height="auto"
								slotMinTime={slotMinTime}
								slotMaxTime={slotMaxTime}
								slotLabelInterval="01:00:00"
								slotLabelFormat={{
									hour: '2-digit',
									minute: '2-digit',
									hour12: false,
								}}
								resourceAreaWidth="15%"
								resourceAreaHeaderContent="Screens"
								slotMinWidth={150}
								eventOrderStrict={true}
								eventOrder="priority"
								nowIndicator={true}
								resources={resources}
								resourceOrder="order"
								events={formattedEvents}
								eventClick={handleEventClick}
								selectable={true}
								selectMirror={true}
								selectOverlap={false}
								select={handleSlotSelect}
								editable={false}
								eventResourceEditable={false}
								schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
								resourceLabelDidMount={(info) => {
									const tr = info.el.closest('tr');
									if (tr && tr.closest('tbody')) tr.style.height = '';
								}}
								resourceLabelContent={(arg) => (
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											height: '100%',
											fontWeight: 700,
											wordBreak: 'break-word',
											overflowWrap: 'break-word',
											whiteSpace: 'normal',
											minWidth: 0,
											textAlign: 'center',
										}}
									>
										{arg.resource.title}
									</div>
								)}
								eventContent={({ event }) => {
									const props = event.extendedProps;

									// Calculate duration in minutes
									const startTime = new Date(event.start);
									const endTime = new Date(event.end);
									const durationInMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

									// Check if it's trivia (use pre-computed value or check again)
									const isTrivia =
										props.isTrivia ||
										props.tmsId?.startsWith('trivia') ||
										props.tmsId?.toLowerCase().includes('trivia') ||
										props.callSign === '0' ||
										props.callSign === 0 ||
										String(props.callSign) === '0' ||
										props.sport?.toLowerCase() === 'trivia';

									// Icon rendering logic (same as TvEventCard)
									// Show icon only if duration > 30 minutes (same condition for all icons including trivia and default)
									let iconHtml = '';
									if (durationInMinutes > 30) {
										if (isTrivia) {
											// Use trivia logo for trivia events
											iconHtml = `<img src="/new_trivia_logo.svg" alt="Trivia Logo" style="width: 32px; height: 32px; object-fit: contain;" />`;
										} else if (props.svgUrl === '/default_logo_sport.svg') {
											iconHtml = `<img style="width:40px; height:auto;" src="${props.svgUrl}" alt="" />`;
										} else {
											iconHtml = props.icon;
										}
									}

									// Truncate text based on duration (same logic as EventSpotlight)
									const truncateText = (text: string | undefined, wordLimit: number) => {
										if (!text) return '';
										const words = text.split(' ');
										return (
											words.slice(0, wordLimit).join(' ') + (words.length > wordLimit ? '...' : '')
										);
									};

									// Get label with truncation based on duration
									let displayLabel = props.eventTitle || event.title;
									if (durationInMinutes <= 30) {
										displayLabel = truncateText(displayLabel, 1);
									} else if (durationInMinutes <= 60) {
										displayLabel = truncateText(displayLabel, 2);
									} else if (durationInMinutes <= 90) {
										displayLabel = truncateText(displayLabel, 6);
									}
									// else: show full label (no truncation)

									return (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												padding: '8px',
												width: '100%',
												minHeight: '140px',
												backgroundColor: 'inherit',
												cursor: 'pointer',
											}}
										>
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													marginBottom: '4px',
												}}
											>
												{durationInMinutes > 30 && iconHtml && (
													<span
														style={{
															fontSize: '2rem',
															fontWeight: '600',
															color: '#fff',
															opacity: 0.9,
															backgroundColor: 'rgb(209,213,219,1)',
															borderRadius: '9999px',
															width: '48px',
															height: '48px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
														}}
													>
														<div dangerouslySetInnerHTML={{ __html: iconHtml }} />
													</span>
												)}
												<span
													style={{
														fontSize: '16px',
														fontWeight: '700',
														color: '#fff',
														opacity: 0.8,
													}}
												>
													{props.channel}
													<br />
													<span style={{ marginTop: '5px', float: 'right' }}>
														{props.startTime}
													</span>
												</span>
											</div>

											<div
												style={{
													justifyContent: 'center',
													width: '100%',
													marginTop: '10px',
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														fontSize: '16px',
														fontWeight: '700',
														color: 'black',
														textAlign: 'center',
														backgroundColor: 'rgb(229,231,235,1)',
														borderRadius: '0.5rem',
														padding: '0.5rem',
													}}
												>
													{displayLabel}
												</div>
											</div>

											<div
												style={{
													fontSize: '16px',
													fontWeight: '700',
													color: 'white',
													marginTop: '0.7rem',
												}}
											>
												{isTrivia ? 'Trivia' : props.sport}
											</div>
										</div>
									);
								}}
							/>
						)}

						{/* Modal */}
						{isModalOpen && selectedEvent && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									backgroundColor: 'rgba(0,0,0,0.5)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 9999,
								}}
							>
								<div
									style={{
										backgroundColor: '#fff',
										padding: '16px 25px',
										borderRadius: '8px',
										width: '32rem',
										boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<h2 style={{ fontSize: '18px', fontWeight: '600' }}>
											{selectedEvent.extendedProps?.eventTitle || selectedEvent.title}
										</h2>
										<button
											type="button"
											className="btn-close-custom"
											onClick={() => setIsModalOpen(false)}
											aria-label="Close"
											style={{ marginLeft: 'auto' }}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
												viewBox="0 0 24 24"
											>
												<path d="M18 6 6 18"></path>
												<path d="m6 6 12 12"></path>
											</svg>
										</button>
									</div>
									<div style={{ marginBottom: '10px' }}>
										<strong>Sport:</strong> {selectedEvent.extendedProps?.sport || '-'}
									</div>
									<div style={{ marginBottom: '10px' }}>
										<strong>Screen Group:</strong>{' '}
										<select
											value={screenGroup}
											onChange={(e) => screenGroupChange(e)}
											style={{ padding: '4px', marginLeft: '5px' }}
										>
											{resources.map((res) => (
												<option key={res.id} value={res.id}>
													{res.title}
												</option>
											))}
										</select>
									</div>
									<div style={{ marginBottom: '10px' }}>
										<strong>Start Time:</strong> {selectedEvent.extendedProps?.startTime || '-'}
									</div>
									<div style={{ marginBottom: '10px' }}>
										<strong>End Time:</strong> {selectedEvent.extendedProps?.endTime || '-'}
									</div>
									<div style={{ marginBottom: '10px' }}>
										<strong>Duration:</strong> {selectedEvent.extendedProps?.duration || 0} minutes
									</div>
									<div style={{ marginBottom: '10px' }}>
										<strong>Channel:</strong> {selectedEvent.extendedProps?.channel || '-'}
									</div>
									<div style={{ marginBottom: '20px' }}>
										<strong>Description:</strong> {selectedEvent.extendedProps?.description || '-'}
									</div>
									<div style={{}}>
										<button
											style={{
												backgroundColor: '#d72638',
												color: '#fff',
												border: 'none',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: 'pointer',
											}}
											onClick={() =>
												deleteGameMasterModel(
													selectedEvent.id,
													selectedEvent.extendedProps?.eventTitle || selectedEvent.title
												)
											}
										>
											Delete
										</button>
										<button
											style={{
												backgroundColor: saveButtonColor,
												color: '#fff',
												border: 'none',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: saveButtonCursor,
												float: 'right',
											}}
											onClick={() => saveEvent(selectedEvent.id, screenGroup)}
											id="saveButton"
											disabled={saveButtonDisabled}
										>
											Save
										</button>
										<button
											style={{
												backgroundColor: '#fff',
												color: '#333',
												border: '1px solid #ccc',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: 'pointer',
												float: 'right',
												marginRight: '15px',
											}}
											onClick={onClose}
										>
											Close
										</button>
									</div>
								</div>
							</div>
						)}
						{isDeleteModalOpen && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									backgroundColor: 'rgba(0,0,0,0.5)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 9999,
								}}
							>
								<div
									style={{
										backgroundColor: '#fff',
										padding: '16px 25px',
										borderRadius: '8px',
										width: '32rem',
										boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<h2 style={{ fontSize: '18px', fontWeight: '500' }}>Confirm Deletion</h2>
										<button
											type="button"
											className="btn-close-custom"
											onClick={() => setIsDeleteModalOpen(false)}
											aria-label="Close"
											style={{ marginLeft: 'auto' }}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
												viewBox="0 0 24 24"
											>
												<path d="M18 6 6 18"></path>
												<path d="m6 6 12 12"></path>
											</svg>
										</button>
									</div>
									<div style={{ textAlign: 'center', marginBottom: '20px' }}>
										<p style={{ fontSize: '16px' }}>Are you sure you want to delete the event</p>
										<strong style={{ fontSize: '16px' }}>{deleteModalTitle}?</strong>
										<p style={{ fontSize: '16px', marginTop: '8px' }}>
											This action cannot be undone.
										</p>
									</div>
									<div>
										<button
											style={{
												backgroundColor: '#fff',
												color: '#333',
												border: '1px solid #ccc',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: 'pointer',
												marginRight: '15px',
											}}
											onClick={onDeleteClose}
										>
											Cancel
										</button>
										<button
											style={{
												backgroundColor: '#d72638',
												color: '#fff',
												border: 'none',
												padding: '6px 12px',
												borderRadius: '4px',
												float: 'right',
												cursor: 'pointer',
											}}
											onClick={() => deleteGameMaster(selectedEvent.id)}
										>
											Confirm
										</button>
									</div>
								</div>
							</div>
						)}

						{/* Print options: report type only – with sports or without sports */}
						{printOptionsOpen && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									backgroundColor: 'rgba(0,0,0,0.5)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 10001,
								}}
								aria-modal="true"
								role="dialog"
								aria-label="Print options"
							>
								<div
									style={{
										backgroundColor: '#fff',
										borderRadius: '8px',
										padding: '24px',
										minWidth: '320px',
										boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
									}}
								>
									<h3 style={{ margin: '0 0 20px', fontSize: '18px' }}>Print Schedule</h3>
									<div style={{ marginBottom: '20px' }}>
										<div style={{ fontWeight: 600, marginBottom: '8px' }}>Report type</div>
										<label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
											<input
												type="radio"
												name="printReportType"
												checked={printWithSports === true}
												onChange={() => setPrintWithSports(true)}
											/>
											Report by Sport
										</label>
										<label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
											<input
												type="radio"
												name="printReportType"
												checked={printWithSports === false}
												onChange={() => setPrintWithSports(false)}
											/>
											Report by Time
										</label>
									</div>
									<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
										<button
											type="button"
											onClick={() => setPrintOptionsOpen(false)}
											style={{
												padding: '8px 16px',
												borderRadius: '4px',
												border: '1px solid #ccc',
												backgroundColor: '#fff',
												cursor: 'pointer',
											}}
										>
											Cancel
										</button>
										<button
											type="button"
											onClick={async () => {
												setPrintOptionsOpen(false);
												await handlePrintSchedule();
											}}
											style={{
												backgroundColor: 'rgb(68, 99, 136)',
												color: 'white',
												padding: '8px 16px',
												borderRadius: '4px',
												border: 'none',
												cursor: 'pointer',
											}}
										>
											Generate PDF
										</button>
									</div>
								</div>
							</div>
						)}

						{/* Print Preview Modal – PDF from JSPDF, works on all browsers including Safari */}
						{printPreviewUrl && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									backgroundColor: 'rgba(0,0,0,0.6)',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'stretch',
									justifyContent: 'center',
									zIndex: 10000,
									padding: '24px',
									boxSizing: 'border-box',
								}}
								aria-modal="true"
								role="dialog"
								aria-label="Print Preview"
							>
								<div
									style={{
										flex: 1,
										display: 'flex',
										flexDirection: 'column',
										minHeight: 0,
										backgroundColor: '#fff',
										borderRadius: '8px',
										overflow: 'hidden',
										boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '12px 16px',
											borderBottom: '1px solid #e0e0e0',
											backgroundColor: '#f5f5f5',
										}}
									>
										<span style={{ fontWeight: 600, fontSize: '16px' }}>Print Preview</span>
										<div style={{ display: 'flex', gap: '8px' }}>
											<button
												type="button"
												onClick={() => downloadPdfFromPreview()}
												style={{
													backgroundColor: 'rgb(68, 99, 136)',
													color: 'white',
													padding: '8px 16px',
													borderRadius: '4px',
													fontSize: '14px',
													cursor: 'pointer',
													border: 'none',
													display: 'flex',
													alignItems: 'center',
													gap: '6px',
												}}
											>
												<PrinterIcon className="w-5 h-5" />
												Print
											</button>
											<button
												type="button"
												onClick={closePrintPreview}
												style={{
													backgroundColor: '#6c757d',
													color: 'white',
													padding: '8px 16px',
													borderRadius: '4px',
													fontSize: '14px',
													cursor: 'pointer',
													border: 'none',
												}}
											>
												Close
											</button>
										</div>
									</div>
									<iframe
										ref={printIframeRef}
										src={printPreviewUrl ? `${printPreviewUrl}#toolbar=0&navpanes=0` : undefined}
										title="Schedule PDF preview"
										style={{
											flex: 1,
											width: '100%',
											border: 'none',
											minHeight: '400px',
										}}
									/>
								</div>
							</div>
						)}

						{/* Program Selection Modal */}
						{isProgramSelectionModalOpen && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									backgroundColor: 'rgba(0,0,0,0.5)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 9999,
								}}
							>
								<div
									style={{
										backgroundColor: '#fff',
										padding: '16px 25px',
										borderRadius: '8px',
										width: '90%',
										maxWidth: '1100px',
										maxHeight: '90vh',
										boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
										<h2 style={{ fontSize: '18px', fontWeight: '600' }}>Select a Program</h2>
										<button
											type="button"
											className="btn-close-custom"
											onClick={() => {
												setIsProgramSelectionModalOpen(false);
												setSelectedProgram(null);
												setProgramsData([]);
											}}
											aria-label="Close"
											style={{ marginLeft: 'auto' }}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												viewBox="0 0 24 24"
											>
												<path d="M18 6 6 18"></path>
												<path d="m6 6 12 12"></path>
											</svg>
										</button>
									</div>

									<div style={{ overflow: 'auto', marginBottom: '20px', maxHeight: '500px' }}>
										{programsData.length > 0 ? (
											<table
												style={{
													width: '100%',
													borderCollapse: 'collapse',
													border: '1px solid #ccc',
												}}
											>
												<thead
													style={{
														backgroundColor: '#f5f5f5',
														position: 'sticky',
														top: 0,
														zIndex: 10,
													}}
												>
													<tr>
														<th
															style={{
																border: '1px solid #ccc',
																padding: '8px',
																textAlign: 'left',
															}}
														></th>
														<th
															style={{
																border: '1px solid #ccc',
																padding: '8px',
																textAlign: 'left',
															}}
														>
															Title
														</th>
														<th
															style={{
																border: '1px solid #ccc',
																padding: '8px',
																textAlign: 'left',
															}}
														>
															Sports
														</th>
														<th
															style={{
																border: '1px solid #ccc',
																padding: '8px',
																textAlign: 'left',
															}}
														>
															Channel
														</th>
														<th
															style={{
																border: '1px solid #ccc',
																padding: '8px',
																textAlign: 'left',
															}}
														>
															Start Time
														</th>
													</tr>
												</thead>
												<tbody>
													{programsData.map((program: any, index: number) => (
														<tr
															key={program.tms_id || index}
															style={{
																cursor: 'pointer',
																backgroundColor:
																	selectedProgram?.tms_id === program.tms_id
																		? '#f0f0f0'
																		: 'transparent',
															}}
															onClick={() => handleProgramRowSelection(program)}
														>
															<td
																style={{
																	border: '1px solid #ccc',
																	padding: '8px',
																	textAlign: 'center',
																}}
															>
																<input
																	type="radio"
																	name="programSelection"
																	checked={selectedProgram?.tms_id === program.tms_id}
																	onChange={() => handleProgramRowSelection(program)}
																	style={{ cursor: 'pointer' }}
																/>
															</td>
															<td style={{ border: '1px solid #ccc', padding: '8px' }}>
																{program.event_title || program.title || '-'}
															</td>
															<td style={{ border: '1px solid #ccc', padding: '8px' }}>
																{program.sport?.label || '-'}
															</td>
															<td style={{ border: '1px solid #ccc', padding: '8px' }}>
																{program.call_sign || '-'}
															</td>
															<td style={{ border: '1px solid #ccc', padding: '8px' }}>
																{formatDateTimeForTable(
																	program.local_start_time || program.start_time
																)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										) : (
											<p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
												No programs available
											</p>
										)}
									</div>

									<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
										<button
											style={{
												backgroundColor: '#fff',
												color: '#333',
												border: '1px solid #ccc',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: 'pointer',
												fontSize: '14px',
											}}
											onClick={() => {
												setIsProgramSelectionModalOpen(false);
												setSelectedProgram(null);
												setProgramsData([]);
											}}
										>
											Close
										</button>
										<button
											style={{
												backgroundColor: 'rgb(68, 99, 136)',
												color: 'white',
												border: 'none',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: selectedProgram ? 'pointer' : 'not-allowed',
												fontSize: '14px',
												opacity: selectedProgram ? 1 : 0.6,
											}}
											disabled={!selectedProgram}
											onClick={handleSaveSelectedProgram}
										>
											Save
										</button>
									</div>
								</div>
							</div>
						)}

						{/* Add Program Modal */}
						{isAddProgramModalOpen && (
							<div
								style={{
									position: 'fixed',
									top: 0,
									left: 0,
									width: '100vw',
									height: '100vh',
									backgroundColor: 'rgba(0,0,0,0.5)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 9999,
								}}
							>
								<div
									style={{
										backgroundColor: '#fff',
										padding: '16px 25px',
										borderRadius: '8px',
										width: '32rem',
										boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center' }}>
										<h2 style={{ fontSize: '18px', fontWeight: '600' }}>Add Program</h2>
										<button
											type="button"
											className="btn-close-custom"
											onClick={() => {
												setIsAddProgramModalOpen(false);
												setSelectedFillSports(undefined);
											}}
											aria-label="Close"
											style={{ marginLeft: 'auto' }}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												viewBox="0 0 24 24"
											>
												<path d="M18 6 6 18"></path>
												<path d="m6 6 12 12"></path>
											</svg>
										</button>
									</div>
									<div style={{ marginTop: '20px' }}>
										<label
											style={{
												display: 'block',
												marginBottom: '8px',
												fontSize: '14px',
												fontWeight: '500',
											}}
										>
											Fill Gap Selection
										</label>
										<div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
											<label
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '8px',
													cursor: 'pointer',
												}}
											>
												<input
													type="radio"
													name="fillGap"
													value="true"
													checked={selectedFillSports === true}
													onChange={() => setSelectedFillSports(true)}
													style={{ width: '16px', height: '16px', cursor: 'pointer' }}
												/>
												Sports Events
											</label>
											<label
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '8px',
													cursor: 'pointer',
												}}
											>
												<input
													type="radio"
													name="fillGap"
													value="false"
													checked={selectedFillSports === false}
													onChange={() => setSelectedFillSports(false)}
													style={{ width: '16px', height: '16px', cursor: 'pointer' }}
												/>
												Non-Sports Events
											</label>
										</div>
										<div style={{ marginBottom: '16px' }}>
											<label
												style={{
													display: 'block',
													marginBottom: '8px',
													fontSize: '14px',
													fontWeight: '500',
												}}
											>
												Screen Group
											</label>
											<input
												readOnly
												value={selectedScreenGroup}
												placeholder="Screen Group"
												style={{
													width: '100%',
													padding: '8px 12px',
													border: '1px solid #ccc',
													borderRadius: '4px',
													fontSize: '14px',
													backgroundColor: '#f5f5f5',
													cursor: 'not-allowed',
												}}
											/>
										</div>
										<div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
											<div style={{ flex: 1 }}>
												<label
													style={{
														display: 'block',
														marginBottom: '8px',
														fontSize: '14px',
														fontWeight: '500',
													}}
												>
													Start Time
												</label>
												<input
													readOnly
													value={formatDateTime(startDatetime)}
													placeholder="Start DateTime"
													style={{
														width: '100%',
														padding: '8px 12px',
														border: '1px solid #ccc',
														borderRadius: '4px',
														fontSize: '14px',
														backgroundColor: '#f5f5f5',
														cursor: 'not-allowed',
													}}
												/>
											</div>
											<div style={{ flex: 1 }}>
												<label
													style={{
														display: 'block',
														marginBottom: '8px',
														fontSize: '14px',
														fontWeight: '500',
													}}
												>
													End Time
												</label>
												<input
													readOnly
													value={formatDateTime(endDatetime)}
													placeholder="End DateTime"
													style={{
														width: '100%',
														padding: '8px 12px',
														border: '1px solid #ccc',
														borderRadius: '4px',
														fontSize: '14px',
														backgroundColor: '#f5f5f5',
														cursor: 'not-allowed',
													}}
												/>
											</div>
										</div>
									</div>
									<div
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: '12px',
											marginTop: '20px',
										}}
									>
										<button
											style={{
												backgroundColor: '#fff',
												color: '#333',
												border: '1px solid #ccc',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor: 'pointer',
												fontSize: '14px',
											}}
											onClick={() => {
												setIsAddProgramModalOpen(false);
												setSelectedFillSports(undefined);
											}}
										>
											Close
										</button>
										<button
											style={{
												backgroundColor: 'rgb(68, 99, 136)',
												color: 'white',
												border: 'none',
												padding: '6px 12px',
												borderRadius: '4px',
												cursor:
													selectedFillSports === undefined || isSubmitting
														? 'not-allowed'
														: 'pointer',
												fontSize: '14px',
												opacity: selectedFillSports === undefined || isSubmitting ? 0.6 : 1,
											}}
											disabled={selectedFillSports === undefined || isSubmitting}
											onClick={handleAddProgramSubmit}
										>
											{isSubmitting ? 'Searching...' : 'Search'}
										</button>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
		</>
	);
};

export default GameMaster;